import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';


const LoadingScreen = ({ onLoadingComplete }) => {
  const [progress, setProgress] = useState(0);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const animationControls = useAnimation();
  const animationRef = useRef(null);
  
  const quotes = [
    "Happiness can be found even in the darkest of times",
    "when one only remembers to turn on the light."
  ];
  
  useEffect(() => {
    // Clear any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    
    let startTime = null;
    const duration = 8000; // Reduced from 15000 to 8000 for faster animation
    
    // Custom easing function that slows down at 25%, 50%, and accelerates from 50% to the end
    const customEase = (t) => {
      // Slow down at 25%
      if (t < 0.25) {
        return t * 0.8;
      }
      // Slow down at 50%
      else if (t < 0.5) {
        return 0.2 + (t - 0.25) * 0.7;
      }
      // Accelerate from 50% to 75% (faster than before)
      else if (t < 0.75) {
        return 0.375 + (t - 0.5) * 0.9;
      }
      // Final stretch - even faster acceleration
      else {
        return 0.6 + (t - 0.75) * 2.4;
      }
    };
    
    const animateProgress = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = customEase(progress);
      const progressValue = Math.floor(easedProgress * 100);
      
      // Skip some numbers - only show multiples of 5
      setProgress(Math.floor(progressValue / 5) * 5);
      
      if (progressValue < 100) {
        animationRef.current = requestAnimationFrame(animateProgress);
      } else {
        onLoadingComplete();
      }
    };
    
    animationRef.current = requestAnimationFrame(animateProgress);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [onLoadingComplete]);
  
  // Handle quote transitions
  useEffect(() => {
    if (currentQuoteIndex >= quotes.length) return;
    
    // Calculate timing for each quote based on loading progress - reduced by half
    const timings = [2000, 4000];
    
    const timer = setTimeout(() => {
      setCurrentQuoteIndex(prevIndex => prevIndex + 1);
    }, timings[currentQuoteIndex]);
    
    return () => clearTimeout(timer);
  }, [currentQuoteIndex, quotes.length]);

  return (
    <motion.div 
      className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50"
      initial={{ opacity: 1 }}
      animate={progress === 100 ? { opacity: 0 } : { opacity: 1 }}
      transition={{ duration: 0.5 }}
      onAnimationComplete={() => {
        if (progress === 100) onLoadingComplete();
      }}
    >
      <style jsx>{`
        /* Candle animation styles */
        html,
        body {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          overflow: hidden;
        }
        
        body {
          background-image: radial-gradient(#150b06, #000000);
        }
        
        .container {
          width: 100%;
          height: 100%;
          position: relative;
          margin-top: 20px;
        }
        
        .emission-point {
          position: absolute;
          left: 50%;
          bottom: 30%;
          transform: translateX(-50%);
          width: 2px;
          height: 2px;
        }
        
        .circle-container {
          position: absolute;
          left: 50%;
          bottom: 30%;
          transform: translateX(-50%);
          animation-timing-function: cubic-bezier(0.25, 0.46, 0.45, 0.94);
          animation-fill-mode: both;
          opacity: 0;
          animation-iteration-count: 1;
        }
        
        .circle {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          mix-blend-mode: screen;
          background-image: radial-gradient(
            hsl(40, 100%, 80%),
            hsl(35, 100%, 70%) 10%,
            hsla(30, 100%, 60%, 0) 56%
          );
          animation: fade-frames 400ms ease-in-out; /* Reduced from 800ms */
          animation-iteration-count: 1;
        }
        
        /* Circle container for the third successful burst get brighter colors */
        .circle-container:nth-child(6) .circle,
        .circle-container:nth-child(7) .circle,
        .circle-container:nth-child(8) .circle,
        .circle-container:nth-child(9) .circle,
        .circle-container:nth-child(10) .circle {
          background-image: radial-gradient(
            hsl(40, 100%, 95%),
            hsl(35, 100%, 85%) 10%,
            hsla(30, 100%, 75%, 0) 56%
          );
        }
        
        @keyframes fade-frames {
          0% {
            opacity: 0.9;
          }
          50% {
            opacity: 0.6;
          }
          100% {
            opacity: 0.9;
          }
        }
        
        @keyframes scale-frames {
          0% {
            transform: scale3d(0.8, 0.8, 1);
          }
          50% {
            transform: scale3d(1.1, 1.1, 1);
          }
          100% {
            transform: scale3d(0.8, 0.8, 1);
          }
        }
        
        /* First attempt at 1000ms - small burst */
        .circle-container:nth-child(1) {
          width: 6px;
          height: 6px;
          animation-name: move-frames-1;
          animation-duration: 700ms; /* Reduced from 1400ms */
          animation-delay: 2000ms; /* Reduced from 4000ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-1 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate3d(-5px, -45vh, 0);
          }
        }
        
        .circle-container:nth-child(2) {
          width: 4px;
          height: 4px;
          animation-name: move-frames-2;
          animation-duration: 1300ms; /* Reduced from 2600ms */
          animation-delay: 4025ms; /* Reduced from 8050ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-2 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate3d(4px, -40vh, 0);
          }
        }
        
        /* Second attempt at 4000ms - medium burst */
        .circle-container:nth-child(3) {
          width: 8px;
          height: 8px;
          animation-name: move-frames-3; 
          animation-duration: 750ms; /* Reduced from 1500ms */
          animation-delay: 3750ms; /* Reduced from 7500ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-3 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate3d(-8px, -60vh, 0);
          }
        }
        
        .circle-container:nth-child(4) {
          width: 6px;
          height: 6px;
          animation-name: move-frames-4;
          animation-duration: 1600ms; /* Reduced from 3200ms */
          animation-delay: 7525ms; /* Reduced from 15050ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-4 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate3d(7px, -55vh, 0);
          }
        }
        
        .circle-container:nth-child(5) {
          width: 5px;
          height: 5px;
          animation-name: move-frames-5;
          animation-duration: 1450ms; /* Reduced from 2900ms */
          animation-delay: 7550ms; /* Reduced from 15100ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-5 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translate3d(0, -50vh, 0);
          }
        }
        
        /* Third successful attempt at 8000ms - large burst */
        .circle-container:nth-child(6) {
          width: 12px;
          height: 12px;
          animation-name: move-frames-6;
          animation-duration: 875ms; /* Reduced from 1750ms */
          animation-delay: 7250ms; /* Reduced from 14500ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-6 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0.8;
            transform: translate3d(-5px, -100vh, 0);
          }
        }
        
        .circle-container:nth-child(7) {
          width: 10px;
          height: 10px;
          animation-name: move-frames-7;
          animation-duration: 1800ms; /* Reduced from 3600ms */
          animation-delay: 14525ms; /* Reduced from 29050ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-7 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0.8;
            transform: translate3d(7px, -100vh, 0);
          }
        }
        
        .circle-container:nth-child(8) {
          width: 14px;
          height: 14px;
          animation-name: move-frames-8;
          animation-duration: 1700ms; /* Reduced from 3400ms */
          animation-delay: 14550ms; /* Reduced from 29100ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-8 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0.8;
            transform: translate3d(0, -100vh, 0);
          }
        }
        
        .circle-container:nth-child(9) {
          width: 11px;
          height: 11px;
          animation-name: move-frames-9;
          animation-duration: 1725ms; /* Reduced from 3450ms */
          animation-delay: 14575ms; /* Reduced from 29150ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-9 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0.8;
            transform: translate3d(-8px, -100vh, 0);
          }
        }
        
        .circle-container:nth-child(10) {
          width: 9px;
          height: 9px;
          animation-name: move-frames-10;
          animation-duration: 1850ms; /* Reduced from 3700ms */
          animation-delay: 14600ms; /* Reduced from 29200ms */
          animation-iteration-count: 1;
        }
        
        @keyframes move-frames-10 {
          0% {
            opacity: 0;
            transform: translate3d(0, 0, 0);
          }
          5% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            opacity: 0.8;
            transform: translate3d(10px, -100vh, 0);
          }
        }
        
        /* Border lighting effect */
        .border-light {
          position: absolute;
          opacity: 0;
        }
        
        /* Top border segments */
        .border-light.top-center {
          top: 0;
          left: 48%;
          width: 4%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.7), hsla(40, 100%, 80%, 0.9), hsla(40, 100%, 80%, 0.7));
          animation: light-top-center 0.15s forwards; /* Reduced from 0.25s */
          animation-delay: 7375ms; /* Reduced from 14750ms */
          animation-iteration-count: 1;
        }
        
        .border-light.top-right1 {
          top: 0;
          left: 52%;
          width: 12%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.7), hsla(40, 100%, 80%, 0.6));
          animation: light-top-right1 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 14775ms; /* Reduced from 29550ms */
          animation-iteration-count: 1;
        }
        
        .border-light.top-left1 {
          top: 0;
          right: 52%;
          width: 12%;
          height: 4px;
          background: linear-gradient(to left, hsla(40, 100%, 80%, 0.7), hsla(40, 100%, 80%, 0.6));
          animation: light-top-left1 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 14800ms; /* Reduced from 29600ms */
          animation-iteration-count: 1;
        }
        
        .border-light.top-right2 {
          top: 0;
          left: 64%;
          width: 18%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.6), hsla(40, 100%, 80%, 0.5));
          animation: light-top-right2 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 14825ms; /* Reduced from 29650ms */
          animation-iteration-count: 1;
        }
        
        .border-light.top-left2 {
          top: 0;
          right: 64%;
          width: 18%;
          height: 4px;
          background: linear-gradient(to left, hsla(40, 100%, 80%, 0.6), hsla(40, 100%, 80%, 0.5));
          animation: light-top-left2 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 14850ms; /* Reduced from 29700ms */
          animation-iteration-count: 1;
        }
        
        /* Right side border */
        .border-light.right-top {
          top: 0;
          right: 0;
          width: 4px;
          height: 20%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.5), hsla(40, 100%, 80%, 0.4));
          animation: light-right-top 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 14875ms; /* Reduced from 29750ms */
          animation-iteration-count: 1;
        }
        
        .border-light.right-middle {
          top: 20%;
          right: 0;
          width: 4px;
          height: 30%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.4), hsla(40, 100%, 80%, 0.3));
          animation: light-right-middle 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 14900ms; /* Reduced from 29800ms */
          animation-iteration-count: 1;
        }
        
        .border-light.right-bottom {
          top: 50%;
          right: 0;
          width: 4px;
          height: 20%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.3), hsla(40, 100%, 80%, 0.2));
          animation: light-right-bottom 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 14925ms; /* Reduced from 29850ms */
          animation-iteration-count: 1;
        }
        
        /* Left side border */
        .border-light.left-top {
          top: 0;
          left: 0;
          width: 4px;
          height: 20%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.5), hsla(40, 100%, 80%, 0.4));
          animation: light-left-top 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 14950ms; /* Reduced from 29900ms */
          animation-iteration-count: 1;
        }
        
        .border-light.left-middle {
          top: 20%;
          left: 0;
          width: 4px;
          height: 30%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.4), hsla(40, 100%, 80%, 0.3));
          animation: light-left-middle 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 14975ms; /* Reduced from 29950ms */
          animation-iteration-count: 1;
        }
        
        .border-light.left-bottom {
          top: 50%;
          left: 0;
          width: 4px;
          height: 20%;
          background: linear-gradient(to bottom, hsla(40, 100%, 80%, 0.3), hsla(40, 100%, 80%, 0.2));
          animation: light-left-bottom 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 15000ms; /* Reduced from 30000ms */
          animation-iteration-count: 1;
        }
        
        /* Bottom border segments */
        .border-light.bottom-right {
          bottom: 30%;
          right: 0;
          width: 25%;
          height: 4px;
          background: linear-gradient(to left, hsla(40, 100%, 80%, 0.2), hsla(40, 100%, 80%, 0.3));
          animation: light-bottom-right 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 15025ms; /* Reduced from 30050ms */
          animation-iteration-count: 1;
        }
        
        .border-light.bottom-left {
          bottom: 30%;
          left: 0;
          width: 25%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.2), hsla(40, 100%, 80%, 0.3));
          animation: light-bottom-left 0.25s forwards; /* Reduced from 0.5s */
          animation-delay: 15050ms; /* Reduced from 30100ms */
          animation-iteration-count: 1;
        }
        
        .border-light.bottom-center-right {
          bottom: 30%;
          left: 25%;
          width: 20%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.3), hsla(40, 100%, 80%, 0.4));
          animation: light-bottom-center-right 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 15075ms; /* Reduced from 30150ms */
          animation-iteration-count: 1;
        }
        
        .border-light.bottom-center-left {
          bottom: 30%;
          right: 25%;
          width: 20%;
          height: 4px;
          background: linear-gradient(to left, hsla(40, 100%, 80%, 0.3), hsla(40, 100%, 80%, 0.4));
          animation: light-bottom-center-left 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 15100ms; /* Reduced from 30200ms */
          animation-iteration-count: 1;
        }
        
        .border-light.bottom-center {
          bottom: 30%;
          left: 45%;
          width: 10%;
          height: 4px;
          background: linear-gradient(to right, hsla(40, 100%, 80%, 0.4), hsla(40, 100%, 80%, 0.7), hsla(40, 100%, 80%, 0.4));
          animation: light-bottom-center 0.2s forwards; /* Reduced from 0.4s */
          animation-delay: 15125ms; /* Reduced from 30250ms */
          animation-iteration-count: 1;
        }
        
        /* Animation keyframes for each border segment */
        @keyframes light-top-center {
          0% {
            opacity: 0;
            width: 0;
            left: 50%;
          }
          100% {
            opacity: 1;
            width: 4%;
            left: 48%;
          }
        }
        
        @keyframes light-top-right1 {
          0% {
            opacity: 0;
            width: 0;
            left: 52%;
          }
          100% {
            opacity: 1;
            width: 12%;
          }
        }
        
        @keyframes light-top-left1 {
          0% {
            opacity: 0;
            width: 0;
            right: 52%;
          }
          100% {
            opacity: 1;
            width: 12%;
          }
        }
        
        @keyframes light-top-right2 {
          0% {
            opacity: 0;
            width: 0;
            left: 64%;
          }
          100% {
            opacity: 1;
            width: 18%;
          }
        }
        
        @keyframes light-top-left2 {
          0% {
            opacity: 0;
            width: 0;
            right: 64%;
          }
          100% {
            opacity: 1;
            width: 18%;
          }
        }
        
        @keyframes light-right-top {
          0% {
            opacity: 0;
            height: 0;
          }
          100% {
            opacity: 1;
            height: 20%;
          }
        }
        
        @keyframes light-right-middle {
          0% {
            opacity: 0;
            height: 0;
            top: 20%;
          }
          100% {
            opacity: 1;
            height: 30%;
          }
        }
        
        @keyframes light-right-bottom {
          0% {
            opacity: 0;
            height: 0;
            top: 50%;
          }
          100% {
            opacity: 1;
            height: 20%;
          }
        }
        
        @keyframes light-left-top {
          0% {
            opacity: 0;
            height: 0;
          }
          100% {
            opacity: 1;
            height: 20%;
          }
        }
        
        @keyframes light-left-middle {
          0% {
            opacity: 0;
            height: 0;
            top: 20%;
          }
          100% {
            opacity: 1;
            height: 30%;
          }
        }
        
        @keyframes light-left-bottom {
          0% {
            opacity: 0;
            height: 0;
            top: 50%;
          }
          100% {
            opacity: 1;
            height: 20%;
          }
        }
        
        @keyframes light-bottom-right {
          0% {
            opacity: 0;
            width: 0;
            right: 0;
          }
          100% {
            opacity: 1;
            width: 25%;
          }
        }
        
        @keyframes light-bottom-left {
          0% {
            opacity: 0;
            width: 0;
            left: 0;
          }
          100% {
            opacity: 1;
            width: 25%;
          }
        }
        
        @keyframes light-bottom-center-right {
          0% {
            opacity: 0;
            width: 0;
            left: 25%;
          }
          100% {
            opacity: 1;
            width: 20%;
          }
        }
        
        @keyframes light-bottom-center-left {
          0% {
            opacity: 0;
            width: 0;
            right: 25%;
          }
          100% {
            opacity: 1;
            width: 20%;
          }
        }
        
        @keyframes light-bottom-center {
          0% {
            opacity: 0;
            width: 0;
            left: 50%;
          }
          100% {
            opacity: 1;
            width: 10%;
            left: 45%;
          }
        }
      `}</style>
      
      <div className="fixed top-1/2 flex justify-center items-center my-10">
        <motion.div 
          className="font-serif text-[1rem] font-bold"
          style={{
            color: '#f59e0b', // yellow-600
            textShadow: '0 0 20px rgba(244, 233, 224, 0.8), 0 0 20px rgba(245, 158, 11, 0.4), 0 0 30px rgba(245, 158, 11, 0.2)',
          }}
          initial={{ scale: 0.8, opacity: 0.5 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0.5 }}
          transition={{ duration: 0.2 }}
        >
          {progress}%
        </motion.div>
      </div>
      
      {/* Quote container with AnimatePresence for smooth transitions */}
      <div className="h-8 w-full fixed top-1/2 flex justify-center items-center">
        <AnimatePresence mode="wait">
          {currentQuoteIndex < quotes.length && (
            <motion.div
              key={currentQuoteIndex}
              className="font-serif text-[1rem] font-light absolute"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 1.5 }}
              style={{
                color: '#f59e0b', // yellow-600
                textShadow: '0 0 20px rgba(244, 233, 224, 0.8), 0 0 20px rgba(245, 158, 11, 0.4), 0 0 30px rgba(245, 158, 11, 0.2)',
              }}
            >
              {quotes[currentQuoteIndex]}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="container">
        <div className="emission-point"></div>
        
        {/* First attempt - small burst */}
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        
        {/* Second attempt - medium burst */}
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        
        {/* Third successful attempt - large burst */}
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        <div className="circle-container">
          <div className="circle"></div>
        </div>
        
        {/* Snake-like border lighting effect */}
        {/* Top segments */}
        {/* <div className='w-screen h-screen absolute top-0 left-0'>
        <div className="border-light top-center"></div>
        <div className="border-light top-right1"></div>
        <div className="border-light top-left1"></div>
        <div className="border-light top-right2"></div>
        <div className="border-light top-left2"></div> */}
        
        {/* Side segments */}
        {/* <div className="border-light right-top"></div>
        <div className="border-light right-middle"></div>
        <div className="border-light right-bottom"></div>
        <div className="border-light left-top"></div>
        <div className="border-light left-middle"></div>
        <div className="border-light left-bottom"></div> */}
        
        {/* Bottom segments */}
        {/* <div className="border-light bottom-right"></div>
        <div className="border-light bottom-left"></div>
        <div className="border-light bottom-center-right"></div>
        <div className="border-light bottom-center-left"></div>
        <div className="border-light bottom-center"></div>
        </div> */}
      </div>
    </motion.div>
  );
};

export default LoadingScreen;